import { create } from 'zustand';
import { api, SalesItem, VendorPayout, VendorStats } from '../lib/api';

interface SalesState {
  sales: SalesItem[];
  totalSales: number;
  totalRevenue: number;
  isLoading: boolean;
  error: string | null;
  payouts: VendorPayout[];
  stats: VendorStats | null;

  fetchSales: () => Promise<void>;
  fetchPayouts: () => Promise<void>;
  fetchStats: () => Promise<VendorStats>;
  requestPayout: (amount: number, method: string, note?: string) => Promise<VendorPayout>;
}

export const useSalesStore = create<SalesState>((set) => ({
  sales: [],
  totalSales: 0,
  totalRevenue: 0,
  isLoading: false,
  error: null,
  payouts: [],
  stats: null,

  fetchSales: async () => {
    set({ isLoading: true, error: null });
    try {
      const result = await api.sales.list({ limit: 200 });
      set({
        sales: result?.sales || [],
        totalSales: result?.total || 0,
        totalRevenue: result?.total_revenue || 0,
        isLoading: false,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch sales';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  fetchPayouts: async () => {
    try {
      const payouts = await api.payouts.list();
      set({ payouts: payouts || [] });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch payouts';
      set({ error: message });
      throw error;
    }
  },

  fetchStats: async () => {
    const stats = await api.stats.get();
    set({ stats });
    return stats;
  },

  requestPayout: async (amount, method, note) => {
    const payout = await api.payouts.request({ amount, method, note });
    set((state) => ({ payouts: [payout, ...state.payouts] }));
    return payout;
  },
}));

export function useSales() {
  const store = useSalesStore();
  return {
    sales: store.sales,
    totalSales: store.totalSales,
    totalRevenue: store.totalRevenue,
    isLoading: store.isLoading,
    error: store.error,
    payouts: store.payouts,
    stats: store.stats,
    fetchSales: store.fetchSales,
    fetchPayouts: store.fetchPayouts,
    fetchStats: store.fetchStats,
    requestPayout: store.requestPayout,
  };
}
